import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet'; // Make sure this package is installed
import { useAuth } from '../context/AuthContext';
import { getBrandConfig, formatCurrency } from '../util/domain-utils';
// import { 
//   getGoogleAdsScript, 
//   getMicrosoftAdsScript,
//   getFacebookPixelScript, 
//   getFacebookNoscriptContent,
//   isGoogleAdsEnabled,
//   isFacebookEnabled,
//   isMicrosoftAdsEnabled
// } from '../util/conversionTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const SuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [error, setError] = useState(null);
  const [paymentProcessor, setPaymentProcessor] = useState('stripe');
  const [conversionTracked, setConversionTracked] = useState(false);
  
  // Get brand configuration
  const brand = getBrandConfig();

  useEffect(() => {
    const fetchSessionDetails = async () => {
      try {
        // Check if this is a Stripe or Razorpay payment
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const paymentId = params.get('payment_id');
        
        if (sessionId) {
          // This is a Stripe payment
          setPaymentProcessor('stripe');
          
          // Use backend to verify the Stripe session
          const response = await axios.get(`${BACKEND_URL}/api/verify-session/${sessionId}`);
          
          if (response.data.success) {
            setSessionDetails(response.data.session);
            
            // Log conversion data for debugging
            console.log('Payment successful:', {
              id: response.data.session.id,
              amount: response.data.session.amount_total / 100,
              currency: response.data.session.currency,
              planType: response.data.session.metadata?.planType || 'subscription'
            });
          } else {
            throw new Error('Failed to verify payment session');
          }
        } else if (paymentId) {
          // This is a Razorpay payment
          setPaymentProcessor('razorpay');
          
          // Fetch Razorpay payment details
          const response = await axios.get(`${BACKEND_URL}/api/razorpay-payment/${paymentId}`);
          
          if (response.data.success) {
            // Format the Razorpay payment into session-compatible format
            const payment = response.data.payment;
            
            // Make a structured session object similar to Stripe's for consistency
            const formattedSession = {
              id: payment.id,
              amount_total: payment.amount,
              currency: payment.currency.toLowerCase(),
              payment_status: payment.status,
              metadata: {
                // Try to extract from notes if available
                planType: payment.notes?.planType || '',
                userId: payment.notes?.userId || user?.uid || '',
                description: payment.notes?.description || 'Razorpay Payment'
              },
              customer_details: {
                email: payment.email || user?.email || ''
              }
            };
            
            setSessionDetails(formattedSession);
            
            // Log conversion data for debugging
            console.log('Payment successful:', {
              id: payment.id,
              amount: payment.amount / 100,
              currency: payment.currency,
              planType: payment.notes?.planType || 'subscription'
            });
          } else {
            throw new Error('Failed to fetch payment details');
          }
        } else {
          throw new Error('No payment session or ID found in URL');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setError(error.message || 'An error occurred while processing your payment');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionDetails();
  }, [location.search, user]);

  // Separate useEffect for tracking the conversion
  // This ensures conversion only fires once, after session data is loaded
  // useEffect(() => {
  //   if (!loading && !error && sessionDetails && !conversionTracked) {
  //     setConversionTracked(true);      
  //     if (isGoogleAdsEnabled()) {
  //       console.log('Google Ads conversion tracking enabled and ready to fire');
  //     }
  //     if (isMicrosoftAdsEnabled()) {
  //       console.log('Microsoft Ads conversion tracking enabled and ready to fire');
  //     }
  //     if (isFacebookEnabled()) {
  //       console.log('Facebook Pixel conversion tracking enabled and ready to fire');
  //     }
  //   }
  // }, [loading, error, sessionDetails, conversionTracked]);
  
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4 py-12">
      {/* Conversion tracking scripts - only inserted when payment is verified successful */}

      {/* {!loading && !error && sessionDetails && (
        <Helmet>
          <script type="text/javascript">
            {`
              // Track Google Ads conversion
              ${isGoogleAdsEnabled() ? getGoogleAdsScript(sessionDetails) : ''}
              
              // Track Microsoft Ads conversion
              ${isMicrosoftAdsEnabled() ? getMicrosoftAdsScript(sessionDetails) : ''}
              
              // Track Facebook Pixel conversion
              ${isFacebookEnabled() ? getFacebookPixelScript(sessionDetails) : ''}
            `}
          </script>
          {isFacebookEnabled() && (
            <noscript>
              {getFacebookNoscriptContent()}
            </noscript>
          )}
        </Helmet>
      )} */}

      <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Processing your payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment details.</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-500 mb-4">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => navigate('/plans')} 
              className="px-6 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 text-green-500 mb-4">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
              <p className="text-gray-600 mb-6">Thank you for subscribing to {brand.name}.</p>
            </div>
            
            {sessionDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-800 mb-3">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">Plan:</span>
                    <span className="text-gray-700">{sessionDetails.metadata?.planType || 'oneMonth'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Amount:</span>
                    <span className="text-gray-700">{formatCurrency(sessionDetails.amount_total, sessionDetails.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Payment Method:</span>
                    <span className="text-gray-700 capitalize">{paymentProcessor}</span>
                  </div>
                  {sessionDetails.customer_details && (
                    <div className="flex justify-between">
                      <span className="font-medium">Email:</span>
                      <span className="text-gray-700">{sessionDetails.customer_details.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <p className="text-center text-gray-600 text-sm mb-6">
              You will receive a confirmation email with your invoice details shortly.
            </p>
            
            <button 
              onClick={goToDashboard} 
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-md font-medium shadow hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
      
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>
          &copy; {new Date().getFullYear()} {brand.name}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default SuccessPage;